import React, { useState, useEffect } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  Chip,
  Avatar,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Paper,
  Stack,
} from '@mui/material'
import {
  Group as GroupIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { userGroupService, UserGroup } from '../../services/userGroupService'
import {
  scheduleService,
  AssignmentTarget,
  TargetPreviewResponse,
} from '../../services/scheduleService'

interface TargetSelectorProps {
  targets: AssignmentTarget[]
  onTargetsChange: (targets: AssignmentTarget[]) => void
  preview: TargetPreviewResponse | null
  onPreviewUpdate: (preview: TargetPreviewResponse | null) => void
  disabled?: boolean
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  )
}

const TargetSelector: React.FC<TargetSelectorProps> = ({
  targets,
  onTargetsChange,
  preview,
  onPreviewUpdate,
  disabled = false,
}) => {
  const [tabValue, setTabValue] = useState(0)
  const [groups, setGroups] = useState<UserGroup[]>([])
  const [departments, setDepartments] = useState<string[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [loadingDepts, setLoadingDepts] = useState(false)
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [userSearch, setUserSearch] = useState('')

  // 選択状態
  const [selectedGroups, setSelectedGroups] = useState<UserGroup[]>([])
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([])
  const [selectedPositions, setSelectedPositions] = useState<string[]>([])
  const [selectedUsers, setSelectedUsers] = useState<any[]>([])

  // 役職一覧
  const [positions, setPositions] = useState<string[]>([])
  const [loadingPositions, setLoadingPositions] = useState(false)

  // データ読み込み
  useEffect(() => {
    const loadGroups = async () => {
      setLoadingGroups(true)
      try {
        const response = await userGroupService.getUserGroups({ limit: 100 })
        setGroups(response.userGroups.filter((g) => g.isActive))
      } catch (error) {
        console.error('グループ読み込みエラー:', error)
      } finally {
        setLoadingGroups(false)
      }
    }

    const loadDepartments = async () => {
      setLoadingDepts(true)
      try {
        const response = await scheduleService.getDepartments()
        setDepartments(response.departments)
      } catch (error) {
        console.error('部署読み込みエラー:', error)
      } finally {
        setLoadingDepts(false)
      }
    }

    const loadPositions = async () => {
      setLoadingPositions(true)
      try {
        const response = await scheduleService.getPositions()
        setPositions(response.positions)
      } catch (error) {
        console.error('役職読み込みエラー:', error)
      } finally {
        setLoadingPositions(false)
      }
    }

    loadGroups()
    loadDepartments()
    loadPositions()
  }, [])

  // ユーザー検索
  useEffect(() => {
    if (userSearch.length < 2) {
      setUsers([])
      return
    }

    const searchUsers = async () => {
      setLoadingUsers(true)
      try {
        const response = await scheduleService.getUsers({ search: userSearch, limit: 20 })
        setUsers(response.users)
      } catch (error) {
        console.error('ユーザー検索エラー:', error)
      } finally {
        setLoadingUsers(false)
      }
    }

    const debounce = setTimeout(searchUsers, 300)
    return () => clearTimeout(debounce)
  }, [userSearch])

  // 選択変更時にtargetsを更新
  useEffect(() => {
    const newTargets: AssignmentTarget[] = []

    if (selectedGroups.length > 0) {
      newTargets.push({ type: 'group', ids: selectedGroups.map((g) => g.id) })
    }
    if (selectedDepartments.length > 0) {
      newTargets.push({ type: 'department', ids: selectedDepartments })
    }
    if (selectedPositions.length > 0) {
      newTargets.push({ type: 'position', ids: selectedPositions })
    }
    if (selectedUsers.length > 0) {
      newTargets.push({ type: 'individual', ids: selectedUsers.map((u) => u.id) })
    }

    onTargetsChange(newTargets)
  }, [selectedGroups, selectedDepartments, selectedPositions, selectedUsers, onTargetsChange])

  // プレビュー更新
  useEffect(() => {
    if (targets.length === 0) {
      onPreviewUpdate(null)
      return
    }

    const updatePreview = async () => {
      setLoadingPreview(true)
      try {
        const result = await scheduleService.previewTargets(targets)
        onPreviewUpdate(result)
      } catch (error) {
        console.error('プレビュー取得エラー:', error)
        onPreviewUpdate(null)
      } finally {
        setLoadingPreview(false)
      }
    }

    const debounce = setTimeout(updatePreview, 500)
    return () => clearTimeout(debounce)
  }, [targets, onPreviewUpdate])

  const handlePositionChange = (position: string) => {
    setSelectedPositions((prev) =>
      prev.includes(position) ? prev.filter((p) => p !== position) : [...prev, position]
    )
  }

  return (
    <Paper variant="outlined" sx={{ mt: 2 }}>
      <Tabs
        value={tabValue}
        onChange={(_, newValue) => setTabValue(newValue)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<GroupIcon />} label="グループ" disabled={disabled} sx={{ minHeight: 64 }} />
        <Tab icon={<BusinessIcon />} label="組織" disabled={disabled} sx={{ minHeight: 64 }} />
        <Tab icon={<BadgeIcon />} label="役職" disabled={disabled} sx={{ minHeight: 64 }} />
        <Tab icon={<PersonIcon />} label="個人" disabled={disabled} sx={{ minHeight: 64 }} />
      </Tabs>

      {/* グループ選択 */}
      <TabPanel value={tabValue} index={0}>
        <Autocomplete
          multiple
          options={groups}
          value={selectedGroups}
          onChange={(_, newValue) => setSelectedGroups(newValue)}
          loading={loadingGroups}
          disabled={disabled}
          getOptionLabel={(option) => option.name}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                <GroupIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option._count?.members || 0}名{option.department && ` | ${option.department}`}
                </Typography>
              </Box>
            </Box>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={`${option.name} (${option._count?.members || 0}名)`}
                avatar={
                  <Avatar>
                    <GroupIcon />
                  </Avatar>
                }
                disabled={disabled}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="グループを選択"
              placeholder="グループ名で検索"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingGroups && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </TabPanel>

      {/* 組織選択 */}
      <TabPanel value={tabValue} index={1}>
        <Autocomplete
          multiple
          options={departments}
          value={selectedDepartments}
          onChange={(_, newValue) => setSelectedDepartments(newValue)}
          loading={loadingDepts}
          disabled={disabled}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                <BusinessIcon fontSize="small" />
              </Avatar>
              <Typography variant="body2">{option}</Typography>
            </Box>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                avatar={
                  <Avatar>
                    <BusinessIcon />
                  </Avatar>
                }
                color="secondary"
                disabled={disabled}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="部署を選択"
              placeholder="部署名で検索"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingDepts && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </TabPanel>

      {/* 役職選択 */}
      <TabPanel value={tabValue} index={2}>
        {loadingPositions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : positions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            登録されている役職がありません
          </Typography>
        ) : (
          <FormGroup>
            {positions.map((position) => (
              <FormControlLabel
                key={position}
                control={
                  <Checkbox
                    checked={selectedPositions.includes(position)}
                    onChange={() => handlePositionChange(position)}
                    disabled={disabled}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main' }}>
                      <BadgeIcon fontSize="small" />
                    </Avatar>
                    <Typography>{position}</Typography>
                  </Box>
                }
              />
            ))}
          </FormGroup>
        )}
      </TabPanel>

      {/* 個人選択 */}
      <TabPanel value={tabValue} index={3}>
        <Autocomplete
          multiple
          options={users}
          value={selectedUsers}
          onChange={(_, newValue) => setSelectedUsers(newValue)}
          loading={loadingUsers}
          disabled={disabled}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onInputChange={(_, value) => setUserSearch(value)}
          filterOptions={(x) => x}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                <PersonIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.email}
                  {option.department && ` | ${option.department}`}
                </Typography>
              </Box>
            </Box>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.name}
                avatar={
                  <Avatar>
                    <PersonIcon />
                  </Avatar>
                }
                color="primary"
                disabled={disabled}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="ユーザーを検索"
              placeholder="名前またはメールで検索（2文字以上）"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingUsers && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={
            userSearch.length < 2 ? '2文字以上入力してください' : '該当するユーザーがいません'
          }
        />
      </TabPanel>

      {/* プレビューサマリー */}
      {(preview || loadingPreview) && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          {loadingPreview ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                対象者を計算中...
              </Typography>
            </Box>
          ) : (
            preview && (
              <Stack spacing={1}>
                <Typography variant="subtitle2">対象者サマリー</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {preview.breakdown.group.count > 0 && (
                    <Chip
                      size="small"
                      icon={<GroupIcon />}
                      label={`グループ: ${preview.breakdown.group.count}名`}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {preview.breakdown.department.count > 0 && (
                    <Chip
                      size="small"
                      icon={<BusinessIcon />}
                      label={`組織: ${preview.breakdown.department.count}名`}
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                  {preview.breakdown.position.count > 0 && (
                    <Chip
                      size="small"
                      icon={<BadgeIcon />}
                      label={`役職: ${preview.breakdown.position.count}名`}
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                  {preview.breakdown.individual.count > 0 && (
                    <Chip
                      size="small"
                      icon={<PersonIcon />}
                      label={`個人: ${preview.breakdown.individual.count}名`}
                      color="default"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Typography variant="body2" color="primary" fontWeight="bold">
                  合計: {preview.totalUsers}名
                  {preview.duplicateCount > 0 && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {` (重複${preview.duplicateCount}名除外済み)`}
                    </Typography>
                  )}
                </Typography>
              </Stack>
            )
          )}
        </Box>
      )}
    </Paper>
  )
}

export default TargetSelector
